/**
 * StatusBadge Component
 *
 * Displays status badges for service lifecycle and tier levels
 */

import '@tylertech/forge/badge';
import {
  ServiceLifecycleStatus,
  ServiceTier,
  TechnologyLifecycleStage,
  TechnologyVersionStatus,
  DataClassification,
  CapabilityLifecycleStatus,
  CapabilityCriticality,
} from '../../graphql/types';
import './StatusBadge.css';

export type BadgeVariant =
  | 'lifecycle'
  | 'tier'
  | 'lifecycleStage'
  | 'classification'
  | 'capabilityLifecycle'
  | 'criticality'
  | 'versionStatus';

export interface StatusBadgeProps {
  /** Status value to display */
  value:
    | ServiceLifecycleStatus
    | ServiceTier
    | TechnologyLifecycleStage
    | TechnologyVersionStatus
    | DataClassification
    | CapabilityLifecycleStatus
    | CapabilityCriticality
    | string;
  /** Badge variant type */
  variant: BadgeVariant;
}

/**
 * Get theme color for lifecycle status
 */
function getLifecycleTheme(status: ServiceLifecycleStatus): string {
  switch (status) {
    case ServiceLifecycleStatus.Production:
      return 'success';
    case ServiceLifecycleStatus.Development:
      return 'info-secondary';
    case ServiceLifecycleStatus.Prototype:
      return 'warning';
    case ServiceLifecycleStatus.Deprecated:
      return 'error-secondary';
    case ServiceLifecycleStatus.Retired:
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Get theme color for tier level
 */
function getTierTheme(tier: ServiceTier): string {
  switch (tier) {
    case ServiceTier.Tier0:
      return 'error'; // Critical
    case ServiceTier.Tier1:
      return 'warning'; // High priority
    case ServiceTier.Tier2:
      return 'info-secondary'; // Medium priority
    case ServiceTier.Tier3:
      return 'default'; // Low priority
    default:
      return 'default';
  }
}

/**
 * Format lifecycle status for display
 */
function formatLifecycle(status: ServiceLifecycleStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Format tier for display
 */
function formatTier(tier: ServiceTier): string {
  if (tier === ServiceTier.Unassigned) {
    return 'Not yet assigned';
  }
  // tier0 -> Tier 0
  return tier.replace(/tier(\d)/, 'Tier $1');
}

/**
 * Get theme color for technology lifecycle stage
 */
function getLifecycleStageTheme(stage: TechnologyLifecycleStage): string {
  switch (stage) {
    case TechnologyLifecycleStage.Adopt:
      return 'success';
    case TechnologyLifecycleStage.Hold:
      return 'warning';
    case TechnologyLifecycleStage.Evaluate:
      return 'info-secondary';
    case TechnologyLifecycleStage.Sunset:
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Get theme color for data classification
 */
function getClassificationTheme(classification: DataClassification): string {
  switch (classification) {
    case DataClassification.Public:
      return 'info-secondary';
    case DataClassification.Internal:
      return 'success';
    case DataClassification.Confidential:
      return 'warning';
    case DataClassification.Restricted:
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Format data classification for display
 */
function formatClassification(classification: DataClassification): string {
  return classification.charAt(0).toUpperCase() + classification.slice(1);
}

/**
 * Get theme color for capability lifecycle status
 */
function getCapabilityLifecycleTheme(status: CapabilityLifecycleStatus): string {
  switch (status) {
    case CapabilityLifecycleStatus.Active:
      return 'success';
    case CapabilityLifecycleStatus.Developing:
      return 'info-secondary';
    case CapabilityLifecycleStatus.Planned:
      return 'warning';
    case CapabilityLifecycleStatus.Mature:
      return 'success';
    case CapabilityLifecycleStatus.Deprecated:
      return 'error-secondary';
    case CapabilityLifecycleStatus.Retired:
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Format capability lifecycle status for display
 */
function formatCapabilityLifecycle(status: CapabilityLifecycleStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Get theme color for capability criticality
 */
function getCriticalityTheme(criticality: CapabilityCriticality): string {
  switch (criticality) {
    case CapabilityCriticality.Critical:
      return 'error';
    case CapabilityCriticality.Important:
      return 'warning';
    case CapabilityCriticality.Supporting:
      return 'info-secondary';
    case CapabilityCriticality.NiceToHave:
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Format capability criticality for display
 */
function formatCriticality(criticality: CapabilityCriticality): string {
  switch (criticality) {
    case CapabilityCriticality.NiceToHave:
      return 'Nice to Have';
    default:
      return criticality.charAt(0).toUpperCase() + criticality.slice(1);
  }
}

/**
 * Format technology lifecycle stage for display
 */
function formatLifecycleStage(stage: TechnologyLifecycleStage): string {
  return stage;
}

/**
 * Get theme color for a TechnologyVersion publish status (issue #1283)
 */
function getVersionStatusTheme(status: TechnologyVersionStatus): string {
  switch (status) {
    case TechnologyVersionStatus.Published:
      return 'success';
    case TechnologyVersionStatus.Draft:
      return 'info-secondary';
    case TechnologyVersionStatus.Deprecated:
      return 'error-secondary';
    default:
      return 'default';
  }
}

/**
 * Format a TechnologyVersion publish status for display
 */
function formatVersionStatus(status: TechnologyVersionStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * Status badge component for displaying lifecycle and tier status
 *
 * @example
 * ```tsx
 * <StatusBadge variant="lifecycle" value={ServiceLifecycleStatus.Production} />
 * <StatusBadge variant="tier" value={ServiceTier.Tier0} />
 * <StatusBadge variant="lifecycleStage" value={TechnologyLifecycleStage.Adopt} />
 * ```
 */
export function StatusBadge({ value, variant }: StatusBadgeProps): JSX.Element {
  let theme: string;
  let displayText: string;

  if (variant === 'lifecycle') {
    theme = getLifecycleTheme(value as ServiceLifecycleStatus);
    displayText = formatLifecycle(value as ServiceLifecycleStatus);
  } else if (variant === 'lifecycleStage') {
    theme = getLifecycleStageTheme(value as TechnologyLifecycleStage);
    displayText = formatLifecycleStage(value as TechnologyLifecycleStage);
  } else if (variant === 'classification') {
    theme = getClassificationTheme(value as DataClassification);
    displayText = formatClassification(value as DataClassification);
  } else if (variant === 'capabilityLifecycle') {
    theme = getCapabilityLifecycleTheme(value as CapabilityLifecycleStatus);
    displayText = formatCapabilityLifecycle(value as CapabilityLifecycleStatus);
  } else if (variant === 'criticality') {
    theme = getCriticalityTheme(value as CapabilityCriticality);
    displayText = formatCriticality(value as CapabilityCriticality);
  } else if (variant === 'versionStatus') {
    theme = getVersionStatusTheme(value as TechnologyVersionStatus);
    displayText = formatVersionStatus(value as TechnologyVersionStatus);
  } else {
    theme = getTierTheme(value as ServiceTier);
    displayText = formatTier(value as ServiceTier);
  }

  const BadgeElement = 'forge-badge' as React.ElementType;

  return (
    <BadgeElement theme={theme} className={`status-badge status-badge-${variant}`}>
      {displayText}
    </BadgeElement>
  );
}
